import { useEffect, useState, useMemo } from 'react';
import FormField from '../common/FormField';
import { NoteDTO, NoteTypeDTO, UserDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { useNoteTypeApi } from '../../hooks/useNoteTypeApi';
import { useUserApi } from '../../hooks/useUserApi';
import { NoteTypeConstants } from '../../constants/NoteTypeConstants';
import Button from '../common/Button';

interface AddNoteProps {
  memberId: string;
  componentId: string;
  recordId: string;
  onSubmit: (note: NoteDTO) => void;
  onCancel: () => void;
  isLoading: boolean;
}

const AddNote: React.FC<AddNoteProps> = ({ memberId, componentId, recordId, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState<NoteDTO>({
    memberId: memberId,
    componentId: componentId,
    recordId: recordId
  });
  const [noteTypes, setNoteTypes] = useState<NoteTypeDTO[]>([]);
  const [admins, setAdmins] = useState<UserDTO[]>([]);

  const { search: searchNoteTypes, isLoading: isLoadingNoteTypes } = useNoteTypeApi();
  const { search: searchUsers, isLoading: isLoadingUsers } = useUserApi();

  const colorOptions = [
    { value: '#3B82F6', label: 'Blue' },
    { value: '#EAB308', label: 'Yellow' },
    { value: '#22C55E', label: 'Green' },
    { value: '#EF4444', label: 'Red' },
    { value: '#A855F7', label: 'Purple' },
    { value: '#EC4899', label: 'Pink' },
    { value: '#14B8A6', label: 'Teal' },
  ];

  // Determine which fields should be shown based on note type
  const selectedNoteTypeId = formData.noteTypeId?.toLowerCase();
  const showColorField = selectedNoteTypeId === NoteTypeConstants.DISCIPLINARY || 
                         selectedNoteTypeId === NoteTypeConstants.TAG;
  const showAssignToField = selectedNoteTypeId === NoteTypeConstants.TECHNICAL_ISSUE || 
                           selectedNoteTypeId === NoteTypeConstants.TODO_TASK;

  // Dynamic validation rules based on note type
  const validationRules: ValidationRules = useMemo(() => {
    const rules: ValidationRules = {
      description: [{ required: true, message: 'Description is required' }],
      noteTypeId: [{ required: true, message: 'Type is required' }],
    };

    if (showColorField) {
      rules.colour = [{ required: true, message: 'Colour is required' }];
    }

    if (showAssignToField) {
      rules.assignedAdminId = [{ required: true, message: 'Assign to is required' }];
    }

    return rules;
  }, [showColorField, showAssignToField]);

  const { errors, validate } = useFormValidation<NoteDTO>(validationRules);

  useEffect(() => {
    const fetchNoteTypes = async () => {
      try {
        const response = await searchNoteTypes({
          page: 1,
          pageSize: 999,
          sortBy: 'Title',
        });
        setNoteTypes(response?.data?.results || []);
      } catch (error) {
        console.log('Error fetching note types: ', error);
      }
    };
    fetchNoteTypes();
  }, []);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const response = await searchUsers({
          filters: {
            role: 'Admin',
          },
          page: 1,
          pageSize: 999,
          sortBy: 'FirstName',
        });
        setAdmins(response?.data?.results || []);
      } catch (error) {
        console.log('Error fetching admins: ', error);
      }
    };
    fetchAdmins();
  }, []);

  // Clear fields when note type changes
  useEffect(() => {
    if (formData.noteTypeId) {
      // Clear color if not needed
      if (!showColorField && formData.colour) {
        setFormData(prev => ({ ...prev, colour: undefined }));
      }
      // Clear assignedAdminId if not needed
      if (!showAssignToField && formData.assignedAdminId) {
        setFormData(prev => ({ ...prev, assignedAdminId: undefined }));
      }
    }
  }, [formData.noteTypeId, showColorField, showAssignToField]);

  const handleSubmit = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  return (
    <>
      <FormField
        id="description"
        label="Note"
        type="textarea"
        value={formData.description || ''}
        onChange={(e) => handleChange(e, setFormData)}
        placeholder="Enter Description"
        required={true}
        error={errors.description}
      />
      <FormField
        id="noteTypeId"
        label="Note Type Id"
        type="dropdown"
        value={formData.noteTypeId || ''}
        onChange={(e) => handleChange(e, setFormData)}
        placeholder="Select Note Type"
        required={true}
        error={errors.noteTypeId}
        options={[
          ...noteTypes.map((noteType) => ({
            value: noteType.id,
            label: noteType.title,
          })),
        ]}
        isLoading={isLoadingNoteTypes}
      />

      {showColorField && (
        <FormField
          id="colour"
          label="Colour"
          type="dropdown"
          value={formData.colour || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Colour"
          required={true}
          error={errors.colour}
          options={colorOptions}
        />
      )}

      {showAssignToField && (
        <FormField
          id="assignedAdminId"
          label="Assign to"
          type="dropdown"
          value={formData.assignedAdminId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Admin"
          required={true}
          error={errors.assignedAdminId}
          options={admins.map((admin) => ({
            value: admin.id,
            label: `${admin.firstName} ${admin.lastName}`,
          }))}
          isLoading={isLoadingUsers}
        />
      )}

      <div className="flex justify-end space-x-4">
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSubmit} isLoading={isLoading}>
          Create
        </Button>
      </div>
    </>
  );
};

export default AddNote;
